<template>
    <div class="vin-plate-repeat-review-page public_box">
        <div class="vin-plate-repeat-review-page__header">
            <div>
                <div class="vin-plate-repeat-review-page__nav">
                    <el-button size="mini" plain @click="goWorkbench">返回核验工作台</el-button>
                    <el-button size="mini" type="text" @click="goDuplicateReview">切换到重复图片核验</el-button>
                    <el-button size="mini" type="text" @click="goSimilarImageReview">切换到相似图片核验</el-button>
                    <el-button size="mini" type="text" @click="goClaimRiskReview">切换到理算风险核验</el-button>
                </div>
                <h2>同 VIN/车牌多次出险核验页面</h2>
                <p>集中查看同一 VIN 或同一车牌多次出险命中组，支持复用既有核验工作台完成人工判定与历史追溯。</p>
            </div>
            <div class="vin-plate-repeat-review-page__filters">
                <el-switch
                    v-model="filters.onlyPending"
                    active-text="只看待核验"
                    @input="handleOnlyPendingToggle"
                />
                <el-radio-group
                    v-model="filters.reviewStatus"
                    size="mini"
                    :disabled="filters.onlyPending"
                    @input="handleReviewStatusChange"
                >
                    <el-radio-button label="pending">待定</el-radio-button>
                    <el-radio-button label="useful">有用</el-radio-button>
                    <el-radio-button label="not-useful">无用</el-radio-button>
                    <el-radio-button label="all">全部</el-radio-button>
                </el-radio-group>
                <el-radio-group
                    v-model="filters.hitDimension"
                    size="mini"
                    @input="handleHitDimensionChange"
                >
                    <el-radio-button label="all">全部命中</el-radio-button>
                    <el-radio-button label="vin">VIN</el-radio-button>
                    <el-radio-button label="plate">车牌</el-radio-button>
                    <el-radio-button label="vin_plate">双重命中</el-radio-button>
                </el-radio-group>
            </div>
        </div>

        <el-alert
            v-if="errorMessage"
            type="error"
            :closable="false"
            :title="errorMessage"
            class="vin-plate-repeat-review-page__alert"
        />

        <el-empty
            v-if="!loading && !records.length"
            description="当前无可核验记录"
        />

        <div v-if="records.length" class="vin-plate-repeat-review-page__batch-bar">
            <div class="vin-plate-repeat-review-page__batch-summary">
                当前页共 {{ records.length }} 条，可一键批量判断
            </div>
            <div class="vin-plate-repeat-review-page__batch-actions">
                <el-radio-group
                    v-model="batchDecision"
                    size="mini"
                    class="vin-plate-repeat-review-page__batch-group"
                >
                    <el-radio-button label="pending">待定</el-radio-button>
                    <el-radio-button label="useful">有用</el-radio-button>
                    <el-radio-button label="not-useful">无用</el-radio-button>
                </el-radio-group>
                <el-button
                    size="mini"
                    type="primary"
                    :loading="batchSaving"
                    @click="handleBatchSaveConfirm"
                >
                    当前页批量保存
                </el-button>
            </div>
        </div>

        <div v-loading="loading" class="vin-plate-repeat-review-page__list">
            <div
                v-for="record in records"
                :key="record.reviewRecordId"
                class="vin-plate-repeat-review-page__card"
            >
                <div class="vin-plate-repeat-review-page__main">
                    <div class="vin-plate-repeat-review-page__top-row">
                        <div class="vin-plate-repeat-review-page__identity">
                            <span class="vin-plate-repeat-review-page__label">命中维度</span>
                            <el-tag size="mini" :type="resolveDimensionTagType(record.hitDimension)">
                                {{ resolveDimensionLabel(record.hitDimension) }}
                            </el-tag>
                            <el-tag size="mini" :type="resolveStatusTagType(record.reviewStatus)">
                                {{ resolveStatusLabel(record.reviewStatus) }}
                            </el-tag>
                        </div>
                        <div class="vin-plate-repeat-review-page__trace">
                            <span>规则摘要：{{ record.ruleSnapshot || "-" }}</span>
                        </div>
                    </div>
                    <div class="vin-plate-repeat-review-page__code-grid">
                        <div class="vin-plate-repeat-review-page__code-item">
                            <span class="vin-plate-repeat-review-page__label">VIN</span>
                            <span>{{ record.vinCode || "-" }}</span>
                        </div>
                        <div class="vin-plate-repeat-review-page__code-item">
                            <span class="vin-plate-repeat-review-page__label">车牌号</span>
                            <span>{{ record.plateNumber || "-" }}</span>
                        </div>
                        <div class="vin-plate-repeat-review-page__code-item">
                            <span class="vin-plate-repeat-review-page__label">案件数</span>
                            <span>{{ record.caseCount || 0 }}</span>
                        </div>
                        <div class="vin-plate-repeat-review-page__code-item">
                            <span class="vin-plate-repeat-review-page__label">累计金额</span>
                            <span>{{ formatAmount(record.totalPayAmount) }}</span>
                        </div>
                        <div class="vin-plate-repeat-review-page__code-item">
                            <span class="vin-plate-repeat-review-page__label">报案跨度</span>
                            <span>{{ record.reportDaySpan || 0 }} 天</span>
                        </div>
                        <div class="vin-plate-repeat-review-page__code-item">
                            <span class="vin-plate-repeat-review-page__label">出险跨度</span>
                            <span>{{ record.accidentDaySpan || 0 }} 天</span>
                        </div>
                    </div>
                    <div class="vin-plate-repeat-review-page__case-row">
                        <span class="vin-plate-repeat-review-page__label">案件号清单</span>
                        <div class="vin-plate-repeat-review-page__case-tags">
                            <el-button
                                v-for="caseNo in resolveCaseNoList(record.caseNoList)"
                                :key="`${record.reviewRecordId}-${caseNo}`"
                                type="text"
                                @click="handleOpenWorkbench(caseNo)"
                            >
                                {{ caseNo }}
                            </el-button>
                        </div>
                    </div>
                    <div class="vin-plate-repeat-review-page__meta">
                        <span>最近原因：{{ record.lastReasonDesc || "无" }}</span>
                        <span>排除快照：{{ record.excludeSnapshot || "无" }}</span>
                    </div>
                    <div v-if="historyVisibleMap[record.reviewRecordId]" class="vin-plate-repeat-review-page__history">
                        <div class="vin-plate-repeat-review-page__history-title">历史变更记录和原因</div>
                        <el-timeline v-if="(historyMap[record.reviewRecordId] || []).length">
                            <el-timeline-item
                                v-for="item in historyMap[record.reviewRecordId]"
                                :key="`${record.reviewRecordId}-${item.historyId}`"
                                :timestamp="item.changedAt"
                            >
                                <div class="vin-plate-repeat-review-page__history-reason">
                                    {{ item.reasonDesc || item.reasonCode || "无原因" }}
                                </div>
                                <div class="vin-plate-repeat-review-page__history-detail">
                                    {{ buildHistorySummary(item) }}
                                </div>
                            </el-timeline-item>
                        </el-timeline>
                        <el-empty
                            v-else-if="!historyLoadingMap[record.reviewRecordId]"
                            :image-size="60"
                            description="暂无历史"
                        />
                    </div>
                </div>
                <div class="vin-plate-repeat-review-page__side">
                    <el-radio-group
                        v-model="draftStatusMap[record.reviewRecordId]"
                        size="mini"
                        class="vin-plate-repeat-review-page__decision-group"
                    >
                        <el-radio-button label="pending">待定</el-radio-button>
                        <el-radio-button label="useful">有用</el-radio-button>
                        <el-radio-button label="not-useful">无用</el-radio-button>
                    </el-radio-group>
                    <el-button
                        size="mini"
                        type="primary"
                        :loading="savingRecordId === record.reviewRecordId"
                        @click="handleSave(record)"
                    >
                        保存
                    </el-button>
                    <el-button size="mini" @click="toggleHistory(record)">
                        {{ historyVisibleMap[record.reviewRecordId] ? "收起历史" : "查看历史" }}
                    </el-button>
                </div>
            </div>
        </div>

        <div class="vin-plate-repeat-review-page__pagination">
            <el-pagination
                background
                layout="prev, pager, next"
                :current-page="filters.pageNo"
                :page-size="filters.pageSize"
                :total="total"
                @current-change="handlePageChange"
            />
        </div>
    </div>
</template>

<script>
import { buildDuplicateCaseJumpUrl } from "@/views/calculatorTool/medicalThreeColumn.duplicateCase";
import {
    getVinPlateRepeatReviewHistory,
    getVinPlateRepeatReviewRecords,
    saveVinPlateRepeatReviewBatchDecision,
    saveVinPlateRepeatReviewDecision,
} from "@/api/vinPlateRepeatReview";

export default {
    name: "VinPlateRepeatReviewPage",
    inject: {
        injectedInterfaceUrl: {
            from: "interfaceUrl",
            default: "",
        },
    },
    data() {
        return {
            loading: false,
            savingRecordId: null,
            batchSaving: false,
            records: [],
            total: 0,
            errorMessage: "",
            batchDecision: "pending",
            draftStatusMap: {},
            historyMap: {},
            historyVisibleMap: {},
            historyLoadingMap: {},
            filters: {
                reviewStatus: "pending",
                hitDimension: "all",
                onlyPending: true,
                pageNo: 1,
                pageSize: 20,
            },
        };
    },
    created() {
        this.hydrateFiltersFromRoute();
        this.fetchRecords();
    },
    methods: {
        getResolvedInterfaceUrl() {
            return this.injectedInterfaceUrl || "";
        },
        goWorkbench() {
            this.$router.push({ path: "/calculatorTool/reviewWorkbench" });
        },
        goDuplicateReview() {
            this.$router.push({ path: "/calculatorTool/duplicateReview" });
        },
        goSimilarImageReview() {
            this.$router.push({ path: "/calculatorTool/similarImageReview" });
        },
        goClaimRiskReview() {
            this.$router.push({ path: "/calculatorTool/claimRiskReview" });
        },
        hydrateFiltersFromRoute() {
            const query = (this.$route && this.$route.query) || {};
            const onlyPending = query.onlyPending !== "false";
            this.filters.onlyPending = onlyPending;
            this.filters.reviewStatus = onlyPending ? "pending" : query.reviewStatus || "all";
            this.filters.hitDimension = query.hitDimension || "all";
            this.filters.pageNo = Number(query.pageNo) > 0 ? Number(query.pageNo) : 1;
        },
        syncRouteQuery() {
            if (!this.$router || !this.$route) {
                return;
            }
            this.$router.replace({
                query: {
                    onlyPending: this.filters.onlyPending ? "true" : "false",
                    reviewStatus: this.filters.reviewStatus,
                    hitDimension: this.filters.hitDimension,
                    pageNo: this.filters.pageNo > 1 ? String(this.filters.pageNo) : undefined,
                },
            }).catch(() => {});
        },
        formatAmount(value) {
            if (value === null || value === undefined || value === "") {
                return "-";
            }
            return Number(value).toFixed(2);
        },
        resolveCaseNoList(caseNoList) {
            if (!caseNoList) {
                return [];
            }
            return String(caseNoList).split(",").map((item) => item.trim()).filter(Boolean);
        },
        resolveStatusLabel(status) {
            return {
                pending: "待定",
                useful: "有用",
                "not-useful": "无用",
            }[status] || "未知";
        },
        resolveStatusTagType(status) {
            return {
                pending: "warning",
                useful: "success",
                "not-useful": "info",
            }[status] || "";
        },
        resolveDimensionLabel(hitDimension) {
            return {
                vin: "VIN 命中",
                plate: "车牌命中",
                vin_plate: "双重命中",
            }[hitDimension] || "未知";
        },
        resolveDimensionTagType(hitDimension) {
            return {
                vin: "primary",
                plate: "danger",
                vin_plate: "success",
            }[hitDimension] || "";
        },
        buildHistorySummary(item) {
            const before = `${item.beforeCaseCount || 0}案/${this.formatAmount(item.beforeTotalPayAmount)}`;
            const after = `${item.afterCaseCount || 0}案/${this.formatAmount(item.afterTotalPayAmount)}`;
            const dimension = `${item.beforeHitDimension || "-"} -> ${item.afterHitDimension || "-"}`;
            return `案件数与金额 ${before} -> ${after}，命中维度 ${dimension}`;
        },
        resolveDecisionLabel(decision) {
            return this.resolveStatusLabel(decision);
        },
        getPendingRecordIds() {
            return this.records
                .filter((item) => item.reviewStatus === "pending")
                .map((item) => item.reviewRecordId);
        },
        clearHistoryCache(reviewRecordId) {
            this.$delete(this.historyMap, reviewRecordId);
            this.$delete(this.historyVisibleMap, reviewRecordId);
            this.$delete(this.historyLoadingMap, reviewRecordId);
        },
        clearHistoryCacheByIds(reviewRecordIds) {
            reviewRecordIds.forEach((reviewRecordId) => {
                this.clearHistoryCache(reviewRecordId);
            });
        },
        handleOnlyPendingToggle() {
            this.filters.reviewStatus = this.filters.onlyPending ? "pending" : "all";
            this.filters.pageNo = 1;
            this.syncRouteQuery();
            this.fetchRecords();
        },
        handleReviewStatusChange() {
            this.filters.pageNo = 1;
            this.syncRouteQuery();
            this.fetchRecords();
        },
        handleHitDimensionChange() {
            this.filters.pageNo = 1;
            this.syncRouteQuery();
            this.fetchRecords();
        },
        handlePageChange(pageNo) {
            this.filters.pageNo = pageNo;
            this.syncRouteQuery();
            this.fetchRecords();
        },
        async fetchRecords() {
            this.loading = true;
            this.errorMessage = "";
            try {
                const response = await getVinPlateRepeatReviewRecords(
                    this.getResolvedInterfaceUrl(),
                    {
                        reviewStatus: this.filters.reviewStatus,
                        hitDimension: this.filters.hitDimension,
                        pageNo: this.filters.pageNo,
                        pageSize: this.filters.pageSize,
                    }
                );
                const data = (response && response.data && response.data.data) || {};
                this.records = data.items || [];
                this.total = Number(data.total || 0);
                this.draftStatusMap = this.records.reduce((accumulator, record) => {
                    accumulator[record.reviewRecordId] = record.reviewStatus || "pending";
                    return accumulator;
                }, {});
            } catch (error) {
                this.records = [];
                this.total = 0;
                this.errorMessage = error && error.message ? error.message : "加载失败，请稍后重试";
            } finally {
                this.loading = false;
            }
        },
        handleOpenWorkbench(caseNo) {
            const url = buildDuplicateCaseJumpUrl("/calculatorTool/medicalThreeColumn", {
                caseNo,
            });
            window.open(url, "_blank");
        },
        async handleSave(record) {
            const decision = this.draftStatusMap[record.reviewRecordId];
            this.savingRecordId = record.reviewRecordId;
            try {
                await saveVinPlateRepeatReviewDecision(
                    this.getResolvedInterfaceUrl(),
                    record.reviewRecordId,
                    { decision }
                );
                this.$message.success("保存成功");
                this.clearHistoryCache(record.reviewRecordId);
                await this.fetchRecords();
            } catch (error) {
                this.$message.error(error && error.message ? error.message : "保存失败");
            } finally {
                this.savingRecordId = null;
            }
        },
        async handleBatchSaveConfirm() {
            const reviewRecordIds = this.getPendingRecordIds();
            if (!reviewRecordIds.length) {
                this.$message.warning("当前页没有待核验记录，无需批量保存");
                return;
            }
            try {
                await this.$confirm(
                    `将处理当前页 ${reviewRecordIds.length} 条待核验记录，状态更新为“${this.resolveDecisionLabel(this.batchDecision)}”，是否继续？`,
                    "批量保存确认",
                    {
                        confirmButtonText: "确认保存",
                        cancelButtonText: "取消",
                        type: "warning",
                    }
                );
            } catch (error) {
                return;
            }
            this.batchSaving = true;
            try {
                await saveVinPlateRepeatReviewBatchDecision(
                    this.getResolvedInterfaceUrl(),
                    {
                        reviewRecordIds,
                        decision: this.batchDecision,
                    }
                );
                this.$message.success("批量保存成功");
                this.clearHistoryCacheByIds(reviewRecordIds);
                await this.fetchRecords();
            } catch (error) {
                this.$message.error(error && error.message ? error.message : "批量保存失败");
            } finally {
                this.batchSaving = false;
            }
        },
        async toggleHistory(record) {
            const visible = !this.historyVisibleMap[record.reviewRecordId];
            this.$set(this.historyVisibleMap, record.reviewRecordId, visible);
            if (!visible || this.historyMap[record.reviewRecordId]) {
                return;
            }
            this.$set(this.historyLoadingMap, record.reviewRecordId, true);
            try {
                const response = await getVinPlateRepeatReviewHistory(
                    this.getResolvedInterfaceUrl(),
                    record.reviewRecordId
                );
                this.$set(
                    this.historyMap,
                    record.reviewRecordId,
                    (response && response.data && response.data.data) || []
                );
            } catch (error) {
                this.$message.error(error && error.message ? error.message : "历史加载失败");
                this.$set(this.historyMap, record.reviewRecordId, []);
            } finally {
                this.$set(this.historyLoadingMap, record.reviewRecordId, false);
            }
        },
    },
};
</script>

<style scoped>
.vin-plate-repeat-review-page {
    padding: 24px;
}

.vin-plate-repeat-review-page__header {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 20px;
}

.vin-plate-repeat-review-page__nav {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 8px;
}

.vin-plate-repeat-review-page__filters {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 10px;
}

.vin-plate-repeat-review-page__alert {
    margin-bottom: 16px;
}

.vin-plate-repeat-review-page__batch-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 16px;
    padding: 12px 16px;
    background: #f7fbff;
    border: 1px solid #d9ecff;
    border-radius: 8px;
}

.vin-plate-repeat-review-page__batch-actions {
    display: flex;
    align-items: center;
    gap: 12px;
}

.vin-plate-repeat-review-page__list {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.vin-plate-repeat-review-page__card {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 190px;
    gap: 18px;
    padding: 18px;
    border: 1px solid #ebeef5;
    border-radius: 10px;
    background: #fff;
}

.vin-plate-repeat-review-page__top-row,
.vin-plate-repeat-review-page__meta,
.vin-plate-repeat-review-page__identity {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    align-items: center;
}

.vin-plate-repeat-review-page__code-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 12px;
    margin: 14px 0;
}

.vin-plate-repeat-review-page__code-item,
.vin-plate-repeat-review-page__case-row {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.vin-plate-repeat-review-page__case-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.vin-plate-repeat-review-page__side {
    display: flex;
    flex-direction: column;
    gap: 12px;
    align-items: stretch;
}

.vin-plate-repeat-review-page__history {
    margin-top: 16px;
    padding-top: 14px;
    border-top: 1px dashed #dcdfe6;
}

.vin-plate-repeat-review-page__history-title {
    margin-bottom: 10px;
    font-weight: 600;
    color: #303133;
}

.vin-plate-repeat-review-page__label {
    color: #909399;
    font-size: 12px;
}

.vin-plate-repeat-review-page__pagination {
    display: flex;
    justify-content: flex-end;
    margin-top: 20px;
}

@media (max-width: 1100px) {
    .vin-plate-repeat-review-page__card {
        grid-template-columns: 1fr;
    }

    .vin-plate-repeat-review-page__code-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}

@media (max-width: 768px) {
    .vin-plate-repeat-review-page {
        padding: 16px;
    }

    .vin-plate-repeat-review-page__header,
    .vin-plate-repeat-review-page__batch-bar {
        flex-direction: column;
        align-items: stretch;
    }

    .vin-plate-repeat-review-page__filters {
        align-items: stretch;
    }

    .vin-plate-repeat-review-page__code-grid {
        grid-template-columns: 1fr;
    }
}
</style>
